import { useCallback, useMemo, useSyncExternalStore } from 'react';
import { createLocalStore } from '../utils/localStore';
import type { ResourceEntry, ResourceEntryKind } from '../utils/resourceIndex';
import { entryKey, selectPinnedEntries } from '../utils/resourceIndex';

/** Pin keys, most recently pinned last. */
const store = createLocalStore<string[]>('odo_resources_pinned', []);

export interface UsePinnedResourcesResult {
    pinnedKeys: string[];
    isPinned: (kind: ResourceEntryKind, id: number) => boolean;
    toggle: (kind: ResourceEntryKind, id: number) => void;
    /** The given entries that are pinned, most recently pinned first. */
    filterPinned: (entries: ResourceEntry[]) => ResourceEntry[];
}

export function usePinnedResources(): UsePinnedResourcesResult {
    const pinnedKeys = useSyncExternalStore(store.subscribe, store.get, store.get);
    const pinnedSet = useMemo(() => new Set(pinnedKeys), [pinnedKeys]);

    const isPinned = useCallback(
        (kind: ResourceEntryKind, id: number) => pinnedSet.has(entryKey(kind, id)),
        [pinnedSet]
    );

    const toggle = useCallback((kind: ResourceEntryKind, id: number) => {
        const key = entryKey(kind, id);
        const current = store.get();
        store.set(
            current.includes(key) ? current.filter((item) => item !== key) : [...current, key]
        );
    }, []);

    const filterPinned = useCallback(
        (entries: ResourceEntry[]) => selectPinnedEntries(pinnedKeys, entries),
        [pinnedKeys]
    );

    return { pinnedKeys, isPinned, toggle, filterPinned };
}
